// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  Injectable,
  OnDestroy,
  Provider,
} from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  Configuration,
  IPublicClientApplication,
  InteractionStatus,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';

/**
 * purpose: configure auth providers
 * in case configuration is given: configure msal
 * in case no configuration is given: use a 100% mock implementation!
 */
export function providers(configuration?: Configuration): Provider[] {
  if (configuration) {
    function MSALInstanceFactory(): IPublicClientApplication {
      return new PublicClientApplication(configuration!);
    }

    function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
      const protectedResourceMap = new Map([
        ['https://graph.microsoft.com/v1.0/me/', ['user.read','GroupMember.Read.All']],
        // TODO: narrow down this pattern
        ['/*', [configuration?.auth.clientId + '/.default']],
      ]);

      return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
      };
    }

    function MSALGuardConfigFactory(): MsalGuardConfiguration {
      return {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: ['user.read', configuration?.auth.clientId + '/.default'],
        },
      };
    }
    return [
      // provide and immediately run our init service
      MSALSeSOInit,
      {
        provide: APP_INITIALIZER,
        useFactory: () => () => ({}),
        deps: [MSALSeSOInit],
        multi: true,
      },
      {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory,
      },
      {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory,
      },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true,
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
    ];
  } else {
    return [];
  }
}

// main logic liftedfrom the sample code
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/aa362f666894d291bf62a53e9d6ab12a4852b60c/samples/msal-angular-v3-samples/angular16-sample-app/src/app/app.component.ts
// put any additional aurg bootstrapping logic here and NOT in some component!
@Injectable()
export class MSALSeSOInit implements OnDestroy {
  destroy$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    // TODO: this account selection logic from the sample
    // is probably important to handle the case where a silent refresh did NOT work
    // does nothing at this point!
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // TODO: this account selection logic from the sample
        // is probably important to handle the case where a silent refresh did NOT work
        // does nothing at this point!
        this.checkAndSetActiveAccount();
      });
  }

  protected checkAndSetActiveAccount(): void {
    /**:
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
